/**
 * mintPayloads — the shared mint-form zod fragments + servicePayload builders,
 * the single source of truth for what the studio Step-2 forms (Apple and
 * Google, travel and general) validate and send to `mintPass`: a per-pass
 * content field added for one studio reaches the other, so the payload
 * assembly cannot live as per-surface copies that drift.
 *
 * Schema fragments are BUILDERS returning plain zod SHAPES (spread into each
 * surface's own `z.object({...})`) so a surface can still add surface-only
 * keys.
 * Every builder takes the demo's `t` translator so validation messages follow
 * the HU/EN language switch; it defaults to the Hungarian translator, so the
 * legacy module-level `z.object({...fragment()})` call sites keep the exact
 * copy they always had. The i18n-aware surfaces rebuild their schema with the
 * CURRENT `t` (useMemo on `t`), and react-hook-form reads the resolver from
 * the latest render's options, so a zod error raised after a language switch
 * renders in the switched language.
 */

import { z } from 'zod';

import { tHu, type Translate } from '../../i18n/messages';

// ─── Shared schema fragments ─────────────────────────────────────────────────

/** Name + email — pairs with the `RecipientFields` cluster. */
export const recipientSchemaFields = (t: Translate = tHu) => ({
  recipientName: z.string().min(2, t('zod.nameRequired')).max(80),
  recipientEmail: z.string().email(t('zod.emailInvalid')),
});

/**
 * Flight identification — pairs with `FlightLookupFields`: the per-field rules
 * `optionalFlightSchemaFields` below applies to a non-blank flight (the studios'
 * flight is opt-in).
 */
export const travelFlightSchemaFields = (t: Translate = tHu) => ({
  flightIata: z
    .string()
    .min(3, t('zod.flightExample'))
    .max(8, t('zod.tooLong'))
    .regex(/^[A-Za-z0-9]+$/, t('zod.lettersDigitsOnly')),
  flightDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, t('zod.dateFormat')),
});

/**
 * The studios' flight fragment — the flight is OPTIONAL at mint (a flightless
 * travel pass mints with no flight payload at all), so blank values must pass
 * validation; non-blank values obey the same rules as the required fragment.
 */
export const optionalFlightSchemaFields = (t: Translate = tHu) => {
  const required = travelFlightSchemaFields(t);
  return {
    flightIata: required.flightIata.optional().or(z.literal('')),
    flightDate: required.flightDate.optional().or(z.literal('')),
  };
};

/** Travel per-pass content — pairs with `TravelContentFields`. */
export const travelContentSchemaFields = (t: Translate = tHu) => ({
  delayThresholdMinutes: z
    .number({ invalid_type_error: t('zod.numberRequired') })
    .int()
    .min(15, t('zod.delayMin'))
    .max(600, t('zod.delayMax')),
  assistancePhone: z.string().max(40).optional().or(z.literal('')),
  loungeInfoText: z.string().max(200).optional().or(z.literal('')),
  esimLinkUrl: z.string().url(t('zod.urlInvalid')).optional().or(z.literal('')),
});

/**
 * Assistance service block — pairs with `AssistanceDataFields`. Translated
 * messages (same idiom as the travel fragment above) so the inline errors the
 * cluster renders explain WHY the submit did nothing.
 */
export const assistanceContentSchemaFields = (t: Translate = tHu) => ({
  loungeName: z.string().min(2, t('zod.loungeNameRequired')).max(80, t('zod.tooLong')),
  loungeCode: z.string().min(1, t('zod.loungeCodeRequired')).max(10, t('zod.tooLong')),
  accessFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, t('zod.dateFormat')),
  accessTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, t('zod.dateFormat')),
  assistancePhone: z.string().min(5, t('zod.hotlineRequired')).max(30, t('zod.tooLong')),
  // OPTIONAL policy identity ("Kötvényszám") — blank passes; a non-blank value
  // ships as servicePayload.policyNumber (the backend surfaces it by exactly
  // this name — same key PassDetail's ServiceDetailsCard already renders).
  policyNumber: z.string().max(40, t('zod.tooLong')).optional().or(z.literal('')),
  esimLinkUrl: z.string().url(t('zod.urlInvalid')).optional().or(z.literal('')),
});

// ─── servicePayload builders ─────────────────────────────────────────────────

export interface TravelContentValues {
  delayThresholdMinutes: number;
  assistancePhone?: string;
  loungeInfoText?: string;
  esimLinkUrl?: string;
}

/**
 * Travel per-pass content overrides → servicePayload (the backend unpacks
 * them via the passContentOverrides handler hook). Blank/whitespace optionals
 * are trimmed OUT — never sent as ''.
 */
export function buildTravelServicePayload(v: TravelContentValues): Record<string, unknown> {
  const servicePayload: Record<string, unknown> = {
    delayThresholdMinutes: v.delayThresholdMinutes,
  };
  if (v.assistancePhone && v.assistancePhone.trim()) {
    servicePayload.assistancePhone = v.assistancePhone.trim();
  }
  if (v.loungeInfoText && v.loungeInfoText.trim()) {
    servicePayload.loungeInfoText = v.loungeInfoText.trim();
  }
  if (v.esimLinkUrl && v.esimLinkUrl.trim()) {
    servicePayload.esimLinkUrl = v.esimLinkUrl.trim();
  }
  return servicePayload;
}

export interface AssistanceContentValues {
  loungeName: string;
  loungeCode: string;
  /** YYYY-MM-DD — expanded to the day's start instant. */
  accessFrom: string;
  /** YYYY-MM-DD — expanded to the day's end instant. */
  accessTo: string;
  assistancePhone: string;
  /** Optional policy number — trimmed into servicePayload.policyNumber, never sent blank. */
  policyNumber?: string;
  esimLinkUrl?: string;
}

/** Assistance form values → the lounge-shaped servicePayload. */
export function buildAssistanceServicePayload(v: AssistanceContentValues): Record<string, unknown> {
  return {
    loungeName: v.loungeName,
    loungeCode: v.loungeCode,
    accessFrom: `${v.accessFrom}T00:00:00Z`,
    accessTo: `${v.accessTo}T23:59:59Z`,
    assistancePhone: v.assistancePhone,
    // Blank/whitespace optionals are trimmed OUT — never sent as '' (same rule
    // as the travel builder above).
    ...(v.policyNumber && v.policyNumber.trim() ? { policyNumber: v.policyNumber.trim() } : {}),
    ...(v.esimLinkUrl ? { esimLinkUrl: v.esimLinkUrl } : {}),
  };
}
